import { textHeight } from './textLayout';

// Geometry in document pixels. A layer is a box (x, y, width, height) rotated around its
// center. Text boxes take their height from the laid-out lines.
const RAD = Math.PI / 180;

export const layerHeight = layer => (layer.type === 'text' ? textHeight(layer) : layer.height);
export const layerBox = layer => ({ x: layer.x, y: layer.y, width: layer.width, height: layerHeight(layer), rotation: layer.rotation || 0 });
export const boxCenter = box => ({ x: box.x + box.width / 2, y: box.y + box.height / 2 });

export function rotate(point, center, degrees) {
  if (!degrees) return { ...point };
  const angle = degrees * RAD;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  return { x: center.x + dx * cos - dy * sin, y: center.y + dx * sin + dy * cos };
}

export function boxCorners(box) {
  const center = boxCenter(box);
  return [
    { x: box.x, y: box.y }, { x: box.x + box.width, y: box.y },
    { x: box.x + box.width, y: box.y + box.height }, { x: box.x, y: box.y + box.height },
  ].map(point => rotate(point, center, box.rotation));
}

// The axis-aligned bounds of a (possibly rotated) box.
export function boxBounds(box) {
  const corners = boxCorners(box);
  const xs = corners.map(point => point.x);
  const ys = corners.map(point => point.y);
  const left = Math.min(...xs);
  const top = Math.min(...ys);
  const right = Math.max(...xs);
  const bottom = Math.max(...ys);
  return { left, top, right, bottom, width: right - left, height: bottom - top, cx: (left + right) / 2, cy: (top + bottom) / 2 };
}
export const layerBounds = layer => boxBounds(layerBox(layer));

export function pointInBox(point, box, tolerance = 0) {
  const local = rotate(point, boxCenter(box), -box.rotation);
  return local.x >= box.x - tolerance && local.x <= box.x + box.width + tolerance && local.y >= box.y - tolerance && local.y <= box.y + box.height + tolerance;
}

// The topmost visible layer under a point. Thin shapes (lines) get a little slack. Locked
// layers (a background photo, a darkening overlay) let clicks through to what lies under
// them, unless `locked` asks for them; they are selected from the layer list.
export function hitTest(page, point, tolerance = 0, { locked = false } = {}) {
  for (let i = page.layers.length - 1; i >= 0; i--) {
    const layer = page.layers[i];
    if (!layer.visible || (layer.locked && !locked)) continue;
    const slack = layer.type === 'shape' && layer.shape === 'line' ? Math.max(tolerance, 12) : tolerance;
    if (pointInBox(point, layerBox(layer), slack)) return layer;
  }
  return null;
}

// Layers whose bounds cross the page edge: text running off the art is the classic broken
// export, so the editor flags it before download.
export function overflowingLayers(page, size, margin = 1) {
  return page.layers.filter(layer => {
    if (!layer.visible || layer.type !== 'text' || !layer.text.trim()) return false;
    const bounds = layerBounds(layer);
    return bounds.left < -margin || bounds.top < -margin || bounds.right > size.width + margin || bounds.bottom > size.height + margin;
  });
}

// Snapping a moving box to the page (edges and center) and to the other layers' edges and
// centers. Returns the correction to apply and the guides to draw, in document pixels.
export function snapBounds(bounds, size, others, threshold) {
  const targetsX = [0, size.width / 2, size.width];
  const targetsY = [0, size.height / 2, size.height];
  others.forEach(other => { targetsX.push(other.left, other.cx, other.right); targetsY.push(other.top, other.cy, other.bottom); });
  const best = (values, targets) => {
    let found = null;
    values.forEach(value => targets.forEach(target => {
      const delta = target - value;
      if (Math.abs(delta) <= threshold && (!found || Math.abs(delta) < Math.abs(found.delta))) found = { delta, at: target };
    }));
    return found;
  };
  const x = best([bounds.left, bounds.cx, bounds.right], targetsX);
  const y = best([bounds.top, bounds.cy, bounds.bottom], targetsY);
  const guides = [];
  if (x) guides.push({ axis: 'x', at: x.at });
  if (y) guides.push({ axis: 'y', at: y.at });
  return { dx: x ? x.delta : 0, dy: y ? y.delta : 0, guides };
}

// Rotation snaps to the right angles within a few degrees (and to 15° steps on request).
export function snapAngle(degrees, step = 0) {
  let angle = ((degrees % 360) + 540) % 360 - 180;
  if (step) return Math.round(angle / step) * step;
  for (const target of [-180, -90, 0, 90, 180]) if (Math.abs(angle - target) <= 4) angle = target;
  return Math.round(angle * 10) / 10;
}
